import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  AddCircleOutline as AddCircleOutlineIcon,
  PlayArrow as PlayArrowIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import LayersIcon from '@mui/icons-material/Layers';
import GroupsIcon from '@mui/icons-material/Groups';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { ethereumService } from '../utils/ethereum';
import { getConfig } from '../config/bundlerConfig';
import { ethers } from 'ethers';
import { useAuth } from '@clerk/clerk-react';
import { useProject } from '../projects/ProjectContext';

const MotionPaper = motion(Paper);

const pillSx = {
  borderRadius: 999,
  px: 2.25,
  minWidth: 0,
  fontWeight: 700,
  textTransform: 'none',
};

const gradientPrimarySx = {
  ...pillSx,
  backgroundImage:
    'linear-gradient(90deg, rgba(168, 85, 247, 1) 0%, rgba(99, 102, 241, 1) 45%, rgba(34, 211, 238, 1) 100%)',
  color: '#08090e',
  boxShadow: '0 8px 28px rgba(168, 85, 247, 0.28)',
  '&:hover': {
    backgroundImage:
      'linear-gradient(90deg, rgba(187, 110, 255, 1) 0%, rgba(115, 122, 255, 1) 45%, rgba(56, 225, 255, 1) 100%)',
    boxShadow: '0 10px 32px rgba(168, 85, 247, 0.35)',
  },
  '&.Mui-disabled': { backgroundImage: 'none', color: alpha('#e9e7ff', 0.45) },
};

const sectionShellSx = (theme, tint) => ({
  position: 'relative',
  borderRadius: 3,
  p: { xs: 2, sm: 2.5 },
  border: `1px solid ${alpha(tint, 0.22)}`,
  background: `linear-gradient(135deg, ${alpha(tint, 0.08)} 0%, ${alpha(theme.palette.background.paper, 0.35)} 50%, transparent 100%)`,
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    borderColor: alpha(tint, 0.32),
    boxShadow: `0 0 0 1px ${alpha(tint, 0.1)}`,
  },
});

const iconTileSx = (tintBG, tintBorder, color) => ({
  width: 46,
  height: 46,
  borderRadius: 2,
  display: 'grid',
  placeItems: 'center',
  flexShrink: 0,
  background: tintBG,
  border: `1px solid ${tintBorder}`,
  color,
});

const TradingOperations = () => {
  const theme = useTheme();
  const { getToken } = useAuth();
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);
  const { currentProjectId, refreshProjects, projects, projectSettings } = useProject();

  const assignedTokenFromDb = useMemo(
    () => (projectSettings?.token_address || projectSettings?.tokenAddress || '').toString().trim(),
    [projectSettings],
  );
  const [liquidityAmount, setLiquidityAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [wallets, setWallets] = useState([]);
  const [walletSwapAmounts, setWalletSwapAmounts] = useState({});
  const [activeToken, setActiveToken] = useState('');
  const [lastPair, setLastPair] = useState('');
  const [liqTxs, setLiqTxs] = useState(null);
  const [liqStatus, setLiqStatus] = useState(null);
  const [lastBundle, setLastBundle] = useState(null);
  const [bundleInclusion, setBundleInclusion] = useState(null);
  const [bundleNowBlock, setBundleNowBlock] = useState(null);

  const [creationWallet, setCreationWallet] = useState({ address: '', balance: '' });
  const [liquidityStatus, setLiquidityStatus] = useState({ hasLiquidity: false, reserveEth: '', reserveToken: '' });
  const [refreshingStatus, setRefreshingStatus] = useState(false);
  /** LaunchToken.tradingEnabled() on-chain — source of truth for locking Enable + Bundle */
  const [onChainTradingLive, setOnChainTradingLive] = useState(false);

  const explorerBase = useMemo(() => {
    const chainId = Number(getConfig()?.network?.chainId || 1);
    if (chainId === 1) return 'https://etherscan.io';
    if (chainId === 11155111) return 'https://sepolia.etherscan.io';
    return 'https://etherscan.io';
  }, []);

  const persistedLaunchComplete = useMemo(() => {
    const p = (projects || []).find((x) => x?.id === currentProjectId);
    return !!(p?.launch_completed_at || p?.launchCompletedAt);
  }, [projects, currentProjectId]);

  /** On-chain trading live, DB launch-complete, or a bundle we submitted is still waiting for inclusion. */
  const enableTradingLocked =
    onChainTradingLive || persistedLaunchComplete || bundleInclusion?.status === 'pending';

  /** Pool already has both sides — do not add again from this UI. */
  const liquidityAddedLocked = liquidityStatus.hasLiquidity;

  useEffect(() => {
    setOnChainTradingLive(false);
    setLastBundle(null);
    setBundleInclusion(null);
    setLiqTxs(null);
    setLiqStatus(null);
  }, [currentProjectId]);

  useEffect(() => {
    if (!currentProjectId) return;

    loadWallets();
    setActiveToken(assignedTokenFromDb);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProjectId, assignedTokenFromDb]);

  const apiFetch = useCallback(
    async (path, init = {}) => {
      const token = await getTokenRef.current();
      if (!token) throw new Error('Not signed in');
      if (!currentProjectId) throw new Error('Select a project first');
      const res = await fetch(`${getConfig()?.SERVER_URL?.url || 'http://localhost:3001'}${path}`, {
        ...init,
        headers: {
          ...(init.headers || {}),
          Authorization: `Bearer ${token}`,
          'X-Project-Id': currentProjectId,
        },
      });
      const text = await res.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        data = {};
      }
      if (!res.ok) throw new Error(data?.error || `Request failed (${res.status})`);
      return data;
    },
    [currentProjectId],
  );

  const refreshStatus = useCallback(async () => {
    if (!activeToken || !ethers.utils.isAddress(activeToken)) {
      setOnChainTradingLive(false);
      return;
    }
    setRefreshingStatus(true);
    try {
      await ethereumService.ensureInitialized();
      const provider = ethereumService.provider;

      const chainLive = await ethereumService.getLaunchTokenTradingEnabled(activeToken);
      setOnChainTradingLive(!!chainLive);

      // Avoid calling refreshProjects on every poll when trading is live — it re-fetches /api/projects
      // (chain sync runs there) and was causing noisy context churn / repeated Bundle page refresh.
      if (chainLive && !persistedLaunchComplete) {
        try {
          await apiFetch('/api/me/project/launch-complete', { method: 'POST' });
          await refreshProjects?.();
        } catch {
          // Still lock UI from chain state if DB sync fails
        }
      }

      // DB said "launch complete" but chain says trading is still off — GET /api/projects reconciles and clears stale rows.
      if (!chainLive && persistedLaunchComplete) {
        try {
          await refreshProjects?.();
        } catch {
          // ignore
        }
      }

      // Creation wallet address + balance
      let cwAddr = '';
      try {
        const cw = await apiFetch('/api/me/creation-wallet');
        cwAddr = (cw?.creationWallet?.address || '').toString().trim();
      } catch {
        cwAddr = '';
      }
      if (cwAddr) {
        const bal = await provider.getBalance(cwAddr);
        setCreationWallet({ address: cwAddr, balance: ethers.utils.formatEther(bal) });
      } else {
        setCreationWallet({ address: '', balance: '' });
      }

      // Pair + reserves (detect existing liquidity)
      const pair = await ethereumService.getUniswapV2PairAddress();
      setLastPair(pair || '');
      if (!pair) {
        setLiquidityStatus({ hasLiquidity: false, reserveEth: '', reserveToken: '' });
        return;
      }

      const pairAbi = [
        'function getReserves() external view returns (uint112 reserve0, uint112 reserve1, uint32 blockTimestampLast)',
        'function token0() external view returns (address)',
        'function token1() external view returns (address)',
      ];
      const pairContract = new ethers.Contract(pair, pairAbi, provider);
      const [r0, r1] = await pairContract.getReserves();
      const [t0, t1] = await Promise.all([pairContract.token0(), pairContract.token1()]);
      const weth = String(getConfig()?.network?.nativeCoin || '').toLowerCase();
      const token = activeToken.toLowerCase();

      let reserveEthWei = ethers.BigNumber.from(0);
      let reserveTokenWei = ethers.BigNumber.from(0);
      if (t0.toLowerCase() === weth && t1.toLowerCase() === token) {
        reserveEthWei = ethers.BigNumber.from(r0);
        reserveTokenWei = ethers.BigNumber.from(r1);
      } else if (t1.toLowerCase() === weth && t0.toLowerCase() === token) {
        reserveEthWei = ethers.BigNumber.from(r1);
        reserveTokenWei = ethers.BigNumber.from(r0);
      }

      const hasLiquidity = reserveEthWei.gt(0) && reserveTokenWei.gt(0);
      setLiquidityStatus({
        hasLiquidity,
        reserveEth: hasLiquidity ? ethers.utils.formatEther(reserveEthWei) : '',
        reserveToken: hasLiquidity ? ethers.utils.formatUnits(reserveTokenWei, 18) : '',
      });
    } catch (e) {
      // Keep UI usable; just don't update status.
      console.warn(e); // eslint-disable-line
    } finally {
      setRefreshingStatus(false);
    }
  }, [activeToken, apiFetch, persistedLaunchComplete, refreshProjects]);

  const refreshStatusRef = useRef(refreshStatus);
  refreshStatusRef.current = refreshStatus;

  useEffect(() => {
    void refreshStatusRef.current();
  }, [activeToken, currentProjectId, persistedLaunchComplete]);

  // After submitting a bundle, poll the first tx hash to detect inclusion.
  useEffect(() => {
    let cancelled = false;
    if (!lastBundle?.expectedTxHashes?.length) return () => {};

    (async () => {
      try {
        setBundleInclusion({ status: 'pending' });
        const first = lastBundle.expectedTxHashes[0];
        for (let i = 0; i < 60; i++) {
          if (cancelled) return;
          const nowBlk = await ethereumService.provider.getBlockNumber().catch(() => null);
          if (nowBlk != null) {
            setBundleNowBlock((prev) => (prev === nowBlk ? prev : nowBlk));
          }
          const s = await ethereumService.getTxStatus(first).catch(() => ({ status: 'pending' }));
          if (s?.status === 'success') {
            setBundleInclusion({ status: 'included' });
            void refreshStatusRef.current();
            return;
          }
          if (s?.status === 'reverted') {
            setBundleInclusion({ status: 'reverted' });
            void refreshStatusRef.current();
            return;
          }
          // if we've passed maxBlock, mark expired
          if (lastBundle?.maxBlock) {
            if (nowBlk && Number(nowBlk) > Number(lastBundle.maxBlock)) {
              setBundleInclusion({ status: 'expired' });
              void refreshStatusRef.current();
              return;
            }
          }
          await new Promise((r) => setTimeout(r, 3000));
        }
      } catch {
        // ignore
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lastBundle]);

  const loadWallets = async () => {
    try {
      console.log('Loading wallets...');
      // Server-only: do not merge localStorage wallet cache here.
      const loadedWallets = await ethereumService.loadWallets({ allowLocalFallback: false });
      console.log('Loaded wallets:', loadedWallets);
      if (loadedWallets && loadedWallets.length > 0) {
        setWallets(loadedWallets);

        // Display-only: show buyAmount coming from DB (Wallet Management)
        const initialSwapAmounts = {};
        loadedWallets.forEach((wallet) => {
          const buy =
            typeof wallet.buyAmount === 'string'
              ? parseFloat(wallet.buyAmount)
              : typeof wallet.buyAmount === 'number'
                ? wallet.buyAmount
                : NaN;
          const resolved = Number.isFinite(buy) ? Math.max(0, buy) : 0;
          initialSwapAmounts[wallet.address] = resolved.toFixed(8);
        });
        setWalletSwapAmounts(initialSwapAmounts);
      } else {
        console.warn('No wallets loaded');
        toast.warning('No wallets found. Please create wallets first.');
      }
    } catch (error) {
      console.error('Error loading wallets:', error);
      toast.error('Failed to load wallets: ' + error.message);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied');
  };

  const handleAddLiquidity = async () => {
    if (liquidityStatus.hasLiquidity) {
      toast.info('Liquidity is already in the pool for this token.');
      return;
    }
    if (!liquidityAmount || isNaN(liquidityAmount) || parseFloat(liquidityAmount) <= 0) {
      toast.error('Please enter a valid amount greater than 0');
      return;
    }

    setLoading(true);
    try {
      const result = await ethereumService.addLiquidity(liquidityAmount);
      if (result.success) {
        if (result?.mode === 'bundle') {
          toast.error('Liquidity bundling is disabled. Use the normal Add liquidity button.');
        } else {
          const h = result?.addLiquidityHash || (Array.isArray(result?.hashes) ? result.hashes[1] : null);
          toast.success(h ? `Liquidity submitted: ${String(h).slice(0, 12)}…` : 'Liquidity submitted');
          setLiqTxs({ approveHash: result?.approveHash || null, addLiquidityHash: result?.addLiquidityHash || null });
          setLiqStatus({ approve: 'pending', add: 'pending' });

          // Pair may not exist until tx mines. Poll in background (does NOT block UI).
          (async () => {
            try {
              for (let i = 0; i < 20; i++) {
                const p = await ethereumService.getUniswapV2PairAddress();
                if (p) {
                  setLastPair(String(p));
                  break;
                }
                await new Promise((r) => setTimeout(r, 3000));
              }
            } catch {
              // ignore
            }
          })();

          // Poll tx status in background so we can tell if it reverted.
          (async () => {
            try {
              for (let i = 0; i < 30; i++) {
                const a = result?.approveHash ? await ethereumService.getTxStatus(result.approveHash) : { status: 'missing' };
                const b = result?.addLiquidityHash ? await ethereumService.getTxStatus(result.addLiquidityHash) : { status: 'missing' };
                setLiqStatus({ approve: a.status, add: b.status });
                if ((a.status !== 'pending') && (b.status !== 'pending')) break;
                await new Promise((r) => setTimeout(r, 3000));
              }
            } catch {
              // ignore
            }
          })();
        }
      } else {
        toast.error(`Failed to add liquidity: ${result.error}`);
      }
    } catch (error) {
      toast.error('Failed to add liquidity: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEnableTrading = async () => {
    if (onChainTradingLive || persistedLaunchComplete) {
      toast.info(
        onChainTradingLive
          ? 'Trading is already live on-chain (launch token).'
          : 'Launch is already marked complete for this project.',
      );
      return;
    }
    if (bundleInclusion?.status === 'pending') {
      toast.info('A bundle is still awaiting inclusion. Wait for confirmation or expiry before submitting again.');
      return;
    }
    let liveNow = false;
    try {
      liveNow = await ethereumService.getLaunchTokenTradingEnabled(activeToken);
    } catch {
      liveNow = false;
    }
    if (liveNow) {
      setOnChainTradingLive(true);
      toast.info('Trading is already live on-chain.');
      try {
        await apiFetch('/api/me/project/launch-complete', { method: 'POST' });
        await refreshProjects?.();
      } catch {
        // Same as refresh path — UI still locks from chain
      }
      return;
    }
    setLoading(true);
    try {
      // Use DB buyAmount values (Wallet Management) when bundling.
      // This avoids any stale localStorage allocations.
      const swapAmountsFromDb = {};
      wallets.forEach((w) => {
        const n = typeof w.buyAmount === 'number' ? w.buyAmount : typeof w.buyAmount === 'string' ? parseFloat(w.buyAmount) : NaN;
        swapAmountsFromDb[w.address] = Number.isFinite(n) ? String(n) : '0';
      });

      // Get bundle details before executing
      const result = await ethereumService.enableTrading(swapAmountsFromDb);
      if (result.success) {
        setLastBundle({
          bundleHash: result?.bundleHash || null,
          expectedTxHashes: result?.expectedTxHashes || [],
          buyTransactions: result?.receipt?.buyTransactions || [],
          targetBlock: result?.receipt?.targetBlock ?? result?.receipt?.result?.targetBlock ?? result?.receipt?.result?.inclusion?.block ?? null,
          maxBlock: result?.receipt?.maxBlock ?? result?.receipt?.result?.maxBlock ?? result?.receipt?.result?.inclusion?.maxBlock ?? null,
        });
        setBundleInclusion({ status: 'pending' });
        toast.success(
          result?.bundleHash
            ? `Bundle submitted: ${String(result.bundleHash).slice(0, 12)}… — launch completes only after trading is live on-chain.`
            : 'Bundle submitted — launch completes only after trading is live on-chain.',
        );
      } else {
        toast.error(`Failed to enable trading: ${result.error}`);
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const purple = theme.palette.primary.main;
  const cyan = theme.palette.secondary.main;
  const success = theme.palette.success.main;

  return (
    <Box sx={{ maxWidth: 1024, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3, alignItems: { md: 'flex-end' }, justifyContent: 'space-between' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 900,
              letterSpacing: 0.6,
              backgroundImage: `linear-gradient(90deg, ${purple} 0%, #6366f1 45%, ${cyan} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Bundle Launch
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 620 }}>
            Buy targets live in Wallet Management. Here you seed liquidity from the creation wallet, then bundle-enable trading using those saved buys.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ alignItems: 'center' }}>
          <Chip
            size="small"
            label={
              enableTradingLocked
                ? onChainTradingLive
                  ? 'Trading live · on-chain'
                  : bundleInclusion?.status === 'pending'
                    ? 'Bundle pending · on-chain'
                    : 'Launch marked complete'
                : 'Trading not enabled'
            }
            sx={{
              fontWeight: 700,
              borderRadius: 2,
              backgroundColor: enableTradingLocked ? alpha(success, 0.18) : alpha('#fff', 0.06),
              border: `1px solid ${enableTradingLocked ? alpha(success, 0.42) : alpha('#fff', 0.14)}`,
              color: enableTradingLocked ? '#bbf7d0' : undefined,
            }}
          />
          <Chip
            size="small"
            label={activeToken ? `Token ${activeToken.slice(0, 6)}…${activeToken.slice(-4)}` : 'Token not set'}
            sx={{
              fontWeight: 700,
              borderRadius: 2,
              fontFamily: activeToken ? 'monospace' : 'inherit',
              backgroundColor: alpha(cyan, 0.12),
              border: `1px solid ${alpha(cyan, 0.28)}`,
            }}
          />
          {activeToken ? (
            <Button
              size="small"
              variant="outlined"
              component="a"
              href={`${explorerBase}/address/${activeToken}`}
              target="_blank"
              rel="noreferrer"
              endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
              sx={{ ...pillSx, borderColor: alpha(purple, 0.4), py: 0.75 }}
            >
              Explorer
            </Button>
          ) : null}
          <Tooltip title="Refresh on-chain status">
            <IconButton
              size="small"
              onClick={() => void refreshStatus()}
              disabled={refreshingStatus || loading}
              sx={{
                border: `1px solid ${alpha('#fff', 0.14)}`,
                borderRadius: 2,
                backgroundColor: alpha('#fff', 0.03),
              }}
            >
              {refreshingStatus ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <MotionPaper
        elevation={0}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        sx={{
          borderRadius: 4,
          overflow: 'hidden',
          border: `1px solid ${alpha(purple, 0.2)}`,
          background: `linear-gradient(165deg, ${alpha(purple, 0.07)} 0%, ${alpha(theme.palette.background.paper, 0.82)} 40%, ${alpha(cyan, 0.04)} 100%)`,
          backdropFilter: 'blur(12px)',
        }}
      >
        <Stack divider={<Divider sx={{ borderColor: alpha('#fff', 0.06) }} />}>
          <Box sx={{ p: { xs: 2, sm: 2.5 }, ...sectionShellSx(theme, purple) }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box
                  sx={iconTileSx(
                    alpha(purple, 0.16),
                    alpha(purple, 0.3),
                    '#ede9fe',
                  )}
                >
                  <AccountBalanceWalletIcon sx={{ opacity: 0.95 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 880 }}>
                    Creation wallet (C‑Master)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Deploy &amp; LP funds route from this wallet. Generate it in Launch Wizard if you have not yet.
                  </Typography>
                  <Box sx={{ mt: 1.25, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    {creationWallet.address ? (
                      <>
                        <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 760, wordBreak: 'break-all' }}>
                          {creationWallet.address}
                        </Typography>
                        <IconButton size="small" aria-label="Copy address" onClick={() => copy(creationWallet.address)} sx={{ border: `1px solid ${alpha('#fff', 0.12)}` }}>
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        Not set — complete Launch Wizard step 1.
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip
                  label={creationWallet.balance ? `${Number(creationWallet.balance).toFixed(4)} ETH` : 'Balance: —'}
                  sx={{
                    fontWeight: 760,
                    borderRadius: 2,
                    backgroundColor: alpha('#fff', 0.06),
                    border: `1px solid ${alpha('#fff', 0.12)}`,
                  }}
                />
                {creationWallet.address ? (
                  <Button
                    size="small"
                    variant="outlined"
                    component="a"
                    href={`${explorerBase}/address/${creationWallet.address}`}
                    target="_blank"
                    rel="noreferrer"
                    endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                    sx={{ ...pillSx, borderColor: alpha(purple, 0.4), py: 0.75 }}
                  >
                    Explorer
                  </Button>
                ) : null}
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ p: { xs: 2, sm: 2.5 }, ...sectionShellSx(theme, cyan) }}>
            <Stack spacing={2.25}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }} justifyContent="space-between">
                <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={iconTileSx(alpha(cyan, 0.14), alpha(cyan, 0.32), '#cffafe')}>
                    <LayersIcon sx={{ opacity: 0.95 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 880 }}>
                      Liquidity
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
                      Token leg is inferred as <strong>100% of total supply</strong> from the creation wallet; you only specify the ETH leg.
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ alignItems: 'center' }}>
                  <Chip
                    label={liquidityStatus.hasLiquidity ? 'Pool · liquidity detected' : 'Pool · empty / unknown'}
                    sx={{
                      fontWeight: 760,
                      borderRadius: 2,
                      backgroundColor: liquidityStatus.hasLiquidity ? alpha(success, 0.16) : alpha('#fff', 0.05),
                      border: `1px solid ${liquidityStatus.hasLiquidity ? alpha(success, 0.4) : alpha('#fff', 0.14)}`,
                      color: liquidityStatus.hasLiquidity ? '#bbf7d0' : undefined,
                    }}
                  />
                  <Chip
                    variant="outlined"
                    label={
                      liquidityStatus.hasLiquidity && liquidityStatus.reserveEth
                        ? `~${Number(liquidityStatus.reserveEth).toFixed(4)} ETH in pair`
                        : 'Reserves: —'
                    }
                    sx={{ fontWeight: 700, borderRadius: 2, borderColor: alpha('#fff', 0.16) }}
                  />
                  {lastPair ? (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        component="a"
                        href={`${explorerBase}/address/${lastPair}`}
                        target="_blank"
                        rel="noreferrer"
                        endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                        sx={{ ...pillSx, borderColor: alpha(cyan, 0.4), py: 0.75 }}
                      >
                        Pair
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        component="a"
                        href={`https://www.dextools.io/app/en/ether/pair-explorer/${lastPair}`}
                        target="_blank"
                        rel="noreferrer"
                        endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                        sx={{ ...pillSx, borderColor: alpha(cyan, 0.35), py: 0.75 }}
                      >
                        DexTools
                      </Button>
                    </>
                  ) : null}
                </Stack>
              </Stack>

              {liquidityAddedLocked ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Liquidity is already live — add-liquidity inputs are locked.
                </Typography>
              ) : null}
              {enableTradingLocked ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {onChainTradingLive
                    ? 'Trading is live on-chain — Enable + Bundle stays off; project completes in the dashboard.'
                    : bundleInclusion?.status === 'pending'
                      ? 'Bundle was submitted to the relay — waiting for the first bundle transaction on-chain. The project is marked complete only after trading is live.'
                      : 'Launch is marked complete for this project — controls stay locked.'}
                </Typography>
              ) : null}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap>
                <TextField
                  label="ETH amount"
                  type="number"
                  size="small"
                  value={liquidityAmount}
                  onChange={(e) => setLiquidityAmount(e.target.value)}
                  disabled={loading || liquidityAddedLocked}
                  sx={{
                    width: { xs: '100%', sm: 220 },
                    '& .MuiOutlinedInput-root': { borderRadius: 999 },
                  }}
                />
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={loading ? <CircularProgress size={18} sx={{ color: '#08090e' }} /> : <AddCircleOutlineIcon />}
                  onClick={handleAddLiquidity}
                  disabled={loading || liquidityAddedLocked}
                  sx={gradientPrimarySx}
                >
                  Add liquidity
                </Button>
                <Button
                  variant="outlined"
                  startIcon={loading ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                  onClick={handleEnableTrading}
                  disabled={loading || !activeToken || enableTradingLocked}
                  sx={{
                    ...pillSx,
                    borderColor: enableTradingLocked ? alpha('#fff', 0.12) : alpha(success, 0.55),
                    color: enableTradingLocked ? alpha('#fff', 0.45) : '#bbf7d0',
                    '&:hover': {
                      borderColor: enableTradingLocked ? undefined : success,
                      backgroundColor: enableTradingLocked ? undefined : alpha(success, 0.08),
                    },
                  }}
                >
                  {enableTradingLocked
                    ? onChainTradingLive
                      ? 'Trading live'
                      : bundleInclusion?.status === 'pending'
                        ? 'Bundle pending…'
                        : 'Launch complete'
                    : 'Enable + Bundle'}
                </Button>
              </Stack>

              {liqTxs ? (
                <Alert severity="info" sx={{ borderRadius: 2, bgcolor: alpha(cyan, 0.06), border: `1px solid ${alpha(cyan, 0.2)}` }}>
                  Approve: {liqTxs.approveHash ? `${liqTxs.approveHash.slice(0, 10)}…` : '—'} ({liqStatus?.approve || 'pending'})
                  <br />
                  Add liquidity: {liqTxs.addLiquidityHash ? `${liqTxs.addLiquidityHash.slice(0, 10)}…` : '—'} ({liqStatus?.add || 'pending'})
                  <br />
                  {liqTxs.approveHash ? (
                    <Box component="a" href={`${explorerBase}/tx/${liqTxs.approveHash}`} target="_blank" rel="noreferrer" sx={{ color: 'secondary.light', mr: 1 }}>
                      View approve →
                    </Box>
                  ) : null}
                  {liqTxs.addLiquidityHash ? (
                    <Box component="a" href={`${explorerBase}/tx/${liqTxs.addLiquidityHash}`} target="_blank" rel="noreferrer" sx={{ color: 'secondary.light' }}>
                      View add liquidity →
                    </Box>
                  ) : null}
                </Alert>
              ) : null}

              {lastBundle ? (
                <Alert severity="warning" sx={{ borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.08), border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}` }}>
                  Bundle id (≠ tx hash): {lastBundle.bundleHash ? `${String(lastBundle.bundleHash).slice(0, 16)}…` : '—'}
                  <br />
                  Target / max / head block: {lastBundle.targetBlock ?? '—'} / {lastBundle.maxBlock ?? '—'} / {bundleNowBlock ?? '—'}
                  <br />
                  Inclusion: <strong>{bundleInclusion?.status || 'pending'}</strong>
                  {bundleInclusion?.status === 'expired' ? (
                    <>
                      <br />
                      Expired bundles never land — “hash not found” on Explorer can be normal.
                    </>
                  ) : null}
                  <br />
                  Expected txs (if included):
                  {(lastBundle.expectedTxHashes || []).slice(0, 10).map((h) => (
                    <Box key={h} component="div" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                      <Box component="a" href={`${explorerBase}/tx/${String(h)}`} target="_blank" rel="noreferrer">
                        {String(h).slice(0, 18)}…
                      </Box>
                    </Box>
                  ))}
                  {Array.isArray(lastBundle.buyTransactions) && lastBundle.buyTransactions.some((t) => t?.skipped) ? (
                    <>
                      <br />
                      Skipped wallets:
                      {lastBundle.buyTransactions
                        .filter((t) => t?.skipped)
                        .slice(0, 10)
                        .map((t) => (
                          <Box key={t.wallet} sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                            {String(t.wallet).slice(0, 10)}… — {t.reason}
                            {t.error ? ` (${String(t.error).slice(0, 120)}…)` : ''}
                          </Box>
                        ))}
                    </>
                  ) : null}
                </Alert>
              ) : null}
            </Stack>
          </Box>

          <Box sx={{ p: { xs: 2, sm: 2.5 }, ...sectionShellSx(theme, '#818cf8') }}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box sx={iconTileSx(alpha('#818cf8', 0.14), alpha('#818cf8', 0.32), '#e0e7ff')}>
                  <GroupsIcon sx={{ opacity: 0.95 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 880 }}>
                    Wallet allocations
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Serialized <strong>buyAmount</strong> per address — Enable + Bundle reads these from the API (not browser cache).
                  </Typography>
                </Box>
              </Stack>

              {wallets.length > 0 ? (
                <>
                  <TableContainer
                    sx={{
                      maxHeight: 420,
                      borderRadius: 2.5,
                      border: `1px solid ${alpha('#fff', 0.1)}`,
                      backgroundColor: alpha('#000', 0.25),
                    }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 800, bgcolor: alpha('#0a0c12', 0.95), borderBottom: `1px solid ${alpha('#fff', 0.08)}` }}>Wallet</TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 800, bgcolor: alpha('#0a0c12', 0.95), borderBottom: `1px solid ${alpha('#fff', 0.08)}` }}
                          >
                            Balance (ETH)
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 800, bgcolor: alpha('#0a0c12', 0.95), borderBottom: `1px solid ${alpha('#fff', 0.08)}` }}
                          >
                            Buy (ETH)
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {wallets.map((wallet) => {
                          const balance = parseFloat(wallet.balance);
                          const shortAddr = `${wallet.address.slice(0, 6)}…${wallet.address.slice(-4)}`;
                          return (
                            <TableRow
                              key={wallet.address}
                              hover
                              sx={{
                                '&:nth-of-type(even) td': { bgcolor: alpha('#fff', 0.02) },
                                '& td': { py: 1.25, borderColor: alpha('#fff', 0.06) },
                              }}
                            >
                              <TableCell>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 750 }}>
                                    {shortAddr}
                                  </Typography>
                                  <IconButton size="small" onClick={() => copy(wallet.address)} aria-label="Copy" sx={{ border: `1px solid ${alpha('#fff', 0.08)}` }}>
                                    <CopyIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">{Number.isFinite(balance) ? balance.toFixed(4) : '—'}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 760, color: cyan }}>
                                  {(walletSwapAmounts[wallet.address] ?? '0.00000000').toString()}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Alert severity="info" sx={{ borderRadius: 2, bgcolor: alpha(purple, 0.06), border: `1px solid ${alpha(purple, 0.22)}` }}>
                    ~0.001 ETH per wallet is reserved for gas. Edit buys in Wallet Management.
                  </Alert>
                </>
              ) : (
                <Alert severity="warning" sx={{ borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.08), border: `1px solid ${alpha(theme.palette.warning.main, 0.28)}` }}>
                  No wallets on this project. Create or import bundle wallets in Wallet Management, then return here.
                </Alert>
              )}
            </Stack>
          </Box>
        </Stack>
      </MotionPaper>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: { xs: 'left', sm: 'center' } }}>
        Explorer links follow your configured chain ({explorerBase.replace('https://', '')}).
      </Typography>
    </Box>
  );
};

export default TradingOperations; 